import React, { useState, useEffect, useContext, useCallback } from 'react';
import {Stack, DropZone, Thumbnail, Text, Checkbox as CheckboxPolaris, FormLayout, Toast} from '@shopify/polaris';
import {FormContext} from '@thinkwell/react.common';
import {TextField, Checkbox} from '@thinkwell/react.polaris';
import {ContentsContext} from '../contexts/Contents'
import {NoteMinor} from '@shopify/polaris-icons';
import EditTags from '../EditTags'
import round from 'lodash/round'

export default function EditContentForm(props) {
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState();
  const toggleToastActive = useCallback(() => setToastActive((toastActive) => !toastActive), []);
  const [file, setFile] = useState();
  const [useFilename, setUseFilename] = useState(false);
  const [filenameFocus, setFilenameFocus] = useState(false);
  const [pathFocus, setPathFocus] = useState(false);

  const form = useContext(FormContext)

  let acceptedType, pathLabel, pathName;
  switch(props.type) {
    case 'Notes':
      acceptedType = 'application/pdf'
      pathLabel = 'Cdn Pdf Path'
      pathName = 'pdf'
      break;
    case 'PlainHtml':
      acceptedType = 'text/html'
      pathLabel = 'Cdn Html Path'
      pathName = 'html'
      break;
  }

  if (!!pathName) {
    // make sure the path ends with the filename extension (pdf/html)
    useEffect(() => {
      const path = form.field(pathName)
      const filename = useFilename ? form.field('filename') : (file && file.name)

      if (!!path && !!filename && !pathFocus && !path.match(new RegExp(`\\.${pathName}$`))) {
        const appendedPath = path + `/${filename}`
        form.onData(pathName)(appendedPath)
        setToastMessage(`Appended filename to ${pathLabel}`)
        setToastActive(true)
      }
    }, [useFilename, file, form, pathFocus])

    // make sure the filename ends with filename extension (pdf/html)
    useEffect(() => {
      const filename = useFilename ? form.field('filename') : (file && file.name)
      if (!!filename && !filenameFocus && !filename.match(new RegExp(`\\.${pathName}$`))) {
        const appendedFilename = `${filename}.${pathName}`
        form.onData('filename')(appendedFilename)
        setToastMessage(`Appended .${pathName} to filename`)
        setToastActive(true)
      }
    }, [useFilename, file, form, filenameFocus])
  }

  const onUseFilenameChange = (value) => {
    setUseFilename(value)
    if (value) {
      form.onData('filename')(file.name)
    }
  }

  const handleDropZoneDrop = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) => {
      var reader = new window.FileReader()
      var file = acceptedFiles[0]
      reader.onload = function () {
        var base64data = reader.result
        form.onData('filedata')(base64data)
        const data = {data: base64data, name: file.name, type: file.type, size: file.size}
        setFile(data)
      }
      reader.readAsDataURL(file)
    },
    [],
  );

  const uploadedFile = file && (
    <Stack>
      <Thumbnail
        size="small"
        alt={file.name}
        source={NoteMinor}
      />
      <div>
        {file.name}{' '}
        <div>{file.size < 1000000 ? `${round(file.size / 1000, 2)} Kb` : `${round(file.size / 1000000, 2)} Mb`}</div>
      </div>
    </Stack>
  );

  return (<FormLayout>
    { toastActive ? <Toast content={toastMessage} onDismiss={toggleToastActive} /> : null }
    <TextField required
      label="Name"
      name="name"
    />
    { pathName ?
      <TextField required
        label={pathLabel}
        name={pathName}
        disabled={!!form.field('id')}
        onFocus={() => setPathFocus(true)}
        onBlur={() => setPathFocus(false)}
      /> : null
    }
    { props.type == 'VideoLecture' ?
      [
        <TextField required
          label="Subject"
          name="subject"
          key="subject"
        />,
        <TextField required
          type="number"
          label="Video ID"
          name="video_id"
          key="video_id"
        />,
        <TextField
          type="number"
          label="Part ID"
          name="part_id"
          key="part_id"
        />,
        <TextField
          type="number"
          label="Duration"
          name="duration"
          key="duration"
        />,
        <TextField required
          type="number"
          label="Width"
          name="width"
          key="width"
        />,
        <TextField required
          type="number"
          label="Height"
          name="height"
          key="height"
        />,
        <TextField
          label="Transcript HTML"
          name="transcript_html"
          key="transcript_html"
        />,
        <TextField
          label="Transcript PDF"
          name="transcript_pdf"
          key="transcript_pdf"
        />,
        <TextField
          label="Transcript SWF"
          name="transcript_swf"
          key="transcript_swf"
        />,
        <Checkbox
          label="HTML5 Playback"
          name="html5"
          key="html5"
        />,
        <Checkbox
          label="SVG Graphics"
          name="svg"
          key="svg"
        />
      ] : null
    }

    <EditTags />

    <DropZone
      label="File"
      allowMultiple={false}
      onDrop={handleDropZoneDrop}
      variableHeight={true}
      accept={acceptedType}
      errorOverlayText={`File type must be .${pathName}`}>
      {uploadedFile}
      {!file ? <DropZone.FileUpload /> : null}
    </DropZone>
    {
      file ?
        <CheckboxPolaris
          label="Change Filename"
          checked={useFilename}
          onChange={onUseFilenameChange}
        /> : null
    }
    {
      file && useFilename ?
        <TextField required
          label="Filename"
          name="filename"
          onFocus={() => setFilenameFocus(true)}
          onBlur={() => setFilenameFocus(false)}
        /> : null
    }
  </FormLayout>)
}
